<script>
   /*************************************************************/
   /* AppPlots component for test app                           */
   /* -----------------------------------                       */
   /* defines plot components, controls and parameters          */
   /*************************************************************/

   import { untrack } from 'svelte';
   import { getDefaults } from 'svelte-controls-basic';
   import { Container, Button } from 'svelte-controls-basic';
   import { c, Vector } from 'mdatools/arrays';
   import { dnorm } from 'mdatools/distributions';

   // global plot router
   import PlotsRouter from '@mdatools-apps/shared/plots/PlotsRouter.svelte';

   // import local plot components
   import PlotFoM from '../plots/PlotFoM.svelte';
   import PlotDiscrimination from '../plots/PlotDiscrimination.svelte';
   import PlotROC from '../plots/PlotROC.svelte';
   import PlotFoMHeatmap from '../plots/PlotFoMHeatmap.svelte';
   import PlotScores from '../plots/PlotScores.svelte';
   import PlotDistance from '../plots/PlotDistance.svelte';

   // import external plot components
   import PlotRegFoM from '@mdatools-apps/shared/plots/PlotFoM.svelte';

   // import local plot controls components
   import { getPlotFoMControls, getPlotDiscriminationControls, getPlotScoresControls, getPlotDistanceControls,
      getPlotROCControls, getPlotFoMHeatmapControls} from '../plots/Controls.svelte.js';

   // import external plot controls components
   import { getPlotFoMControls as getPlotRegFoMControls} from '@mdatools-apps/shared/plots/Controls.svelte.js';



   let {
      res,
      model,
      data,
      markOutlier,
      markRelabeled,
      showContributions,
      setModelParams,
      statMat
   } = $props();


   const plsres = $derived({'test': res.test.pls});
   const dares = $derived({'test': res.test.da});
   const ncomp = $derived(model.da.ncomp);


   // JSON with plot components (scope is needed for providing correct properties)
   const plots = $derived({
      // objects
      pred: {label: 'Discrimination', scope: 'objects', component: PlotDiscrimination, buttons: buttons1},
      fom: {label: 'Figures of merit (plots)', scope: 'stats', component: PlotFoM},
      fomhm: {label: 'Figures of merit (heatmap)', scope: 'options', component: PlotFoMHeatmap, buttons: buttons3},
      roc: {label: 'ROC curve', scope: 'stats', component: PlotROC},

      // statistics
      fom2: {label: 'RMSE', scope: 'stats', component: PlotRegFoM},
      scores: {label: 'Scores', scope: 'objects', component: PlotScores, buttons: buttons1},
      distance: {label: 'Distance', scope: 'objects', component: PlotDistance, buttons: buttons1},
   })

   // the controls are taken once, from the model and the results this component is mounted
   // with, and untrack marks that as deliberate: the tab keys this component on the model and
   // mounts it behind an '{#if}' on the results, which every reset nulls first, so a new model
   // or a new set arrives as a new component; the DA result alone is replaced in place, and the
   // one descriptor built from it reads its keys and nothing of its content.
   // the controls are a plain object: the descriptors are never changed after they are made,
   // so there is nothing here for $state to track
   const controls = untrack(() => ({
      pred: getPlotDiscriminationControls(['test'], model.da.classNames),
      fom: getPlotFoMControls(dares, model.da.classNames),
      fomhm: getPlotFoMHeatmapControls(['test']),
      roc: getPlotROCControls(['test']),

      fom2: getPlotRegFoMControls(plsres, 1, ['rmse']),
      scores: getPlotScoresControls(plsres),
      distance: getPlotDistanceControls(plsres),
   }));

   // which controls do not apply to what the user has currently selected. the factories set
   // 'hidden' for what the data rules out, and Widget combines the two with OR. the outliers
   // zone is drawn under the soft rule only, and the rule is a model setting, not a plot
   // parameter: Widget runs the predicate in a derived while it renders, so the read of the
   // model in it is tracked like a read of the selection
   controls.pred.showOutliers.hiddenWhen = () => model.da.rule === 'hard';

   // JSON with currently selected plots parameters
   let params = $state({
      pred: getDefaults(controls.pred),
      fom: getDefaults(controls.fom),
      fomhm: getDefaults(controls.fomhm),
      roc: getDefaults(controls.roc),

      fom2: getDefaults(controls.fom2),
      scores: getDefaults(controls.scores),
      distance: getDefaults(controls.distance),
   });


   // combine main elements to be shared with all plots together
   const zscores = c(
      Vector.seq(-10.0, -3, 0.5),
      Vector.seq(-3, 3, 0.01),
      Vector.seq(3.0, 10, 0.5),
   );

   const densnorm = dnorm(zscores);
   const props = $derived({res:plsres, classres:dares, model:model.pls, classmodel: model.da, data,
      ncomp, zscores, densnorm, statMat});

   // combine all callbacks together
   const name = $derived(model.da.rule === 'hard' ? 'tau' : 'alpha');
</script>

{#snippet buttons1(sel)}
   <Container label="Selected">
      <Button onclick={() => showContributions(sel)} text="plot" />
      <Button onclick={() => markOutlier(sel)} text="remove" class="destructive" />
      <Button onclick={() => markRelabeled(sel)} text="relabel" />
   </Container>
{/snippet}

{#snippet buttons3(opt)}
   {@const pstr = model.da.rule === 'hard' ? opt[1].toFixed(2) : opt[1].toFixed(0) + '%'}
   <Container labelWidth="auto" label="Selected: nLV = {opt[0]}, {name} = {pstr}">
      <Button onclick={() => setModelParams(opt)} text="Set" />
   </Container>
{/snippet}


<PlotsRouter
   {plots}
   {props}
   {controls}
   bind:params={params}
/>

<style>
   :global(.tab-content-test .plot-controls-area > .plot-selector-fom2) {
      margin-top: 1em;
   }
</style>